import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  CalendarDays,
  Clock3,
  ChevronRight,
  Eraser,
  Handshake,
  Image as ImageIcon,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageCircle,
  Plus,
  Receipt,
  Settings,
  ShieldCheck,
  Truck,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Logo } from '@/components/Logo';
import { FleetAlertsBanner } from '@/components/FleetAlertsBanner';
import { useCompanySettings, faviconSrc } from '@/lib/companySettings';
import { canAccess, roleLabel, isReadOnly } from '@/lib/roles';
import { NotificationsBell } from '@/components/NotificationsBell';
import OperationalServiceReminder from '@/components/OperationalServiceReminder';

const NAV_GROUPS = [
  {
    label: 'Operaciones',
    items: [
      { to: '/dispatch', label: 'Despacho', shortLabel: 'Despacho', icon: LayoutDashboard, roles: ['admin', 'dispatcher', 'customer_service', 'super_admin'] },
      { to: '/admin/dashboard-ejecutivo', label: 'Resumen ejecutivo', shortLabel: 'Resumen', icon: BarChart3, roles: ['admin', 'dispatcher', 'super_admin'] },
      { to: '/fleet', label: 'Flota', shortLabel: 'Flota', icon: Truck, roles: ['admin', 'dispatcher', 'super_admin'] },
      { to: '/drivers', label: 'Conductores', shortLabel: 'Choferes', icon: Users, roles: ['admin', 'dispatcher', 'super_admin'] },
    ],
  },
  {
    label: 'Comunicación',
    items: [
      { to: '/admin/whatsapp-conversations', label: 'WhatsApp', icon: MessageCircle, roles: ['admin', 'super_admin'] },
      { to: '/admin/whatsapp-ops-health', label: 'Estado del canal', icon: Activity, roles: ['admin', 'super_admin'] },
    ],
  },
  {
    label: 'Administración',
    items: [
      { to: '/invoices', label: 'Facturas', icon: Receipt, roles: ['admin', 'dispatcher', 'super_admin', 'accountant'] },
      { to: '/reports', label: 'Reportes', icon: BarChart3, roles: ['admin', 'dispatcher', 'super_admin', 'accountant'] },
      { to: '/admin/accounting', label: 'Contabilidad', icon: BookOpen, roles: ['admin', 'super_admin', 'accountant'] },
      { to: '/admin/partners', label: 'Aliados', icon: Handshake, roles: ['admin', 'dispatcher', 'super_admin'] },
    ],
  },
  {
    label: 'Gestión',
    items: [
      { to: '/fleet/maintenance', label: 'Mantenimiento', icon: Wrench, roles: ['admin', 'dispatcher', 'super_admin'] },
      { to: '/gallery', label: 'Galería', icon: ImageIcon, roles: ['admin', 'dispatcher', 'super_admin'] },
      { to: '/admin/settings', label: 'Configuración', icon: Settings, roles: ['admin', 'super_admin'] },
      { to: '/profiles', label: 'Probar perfiles', icon: Users, roles: ['super_admin'] },
      { to: '/admin/security', label: 'Seguridad', icon: ShieldCheck, roles: ['admin', 'super_admin'] },
      { to: '/admin/data-cleanup', label: 'Limpieza de pruebas', icon: Eraser, roles: ['super_admin'] },
    ],
  },
];

export function AppShell({ children, title }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const nav = useNavigate();
  const location = useLocation();
  const cs = useCompanySettings();
  const navRef = useRef(null);

  const groups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => canAccess(user, item.roles, item.to)),
      })).filter((group) => group.items.length),
    [user]
  );

  const mobileItems = groups.flatMap((group) => group.items).slice(0, 4);
  const canCreate = canAccess(user, ['admin', 'dispatcher', 'customer_service'], '/dispatch/nuevo') && !isReadOnly(user);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const formattedDate = new Intl.DateTimeFormat('es-DO', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  }).format(now);
  const formattedTime = new Intl.DateTimeFormat('es-DO', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(now);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    el.scrollTop = Number(sessionStorage.getItem('ca_nav_scroll') || 0);
    const onScroll = () => sessionStorage.setItem('ca_nav_scroll', String(el.scrollTop));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const href = faviconSrc(cs);
    if (!href) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  }, [cs?.favicon_url, cs?.compact_logo_url, cs?.logo_url]);

  return (
    <div className="ca-shell" data-testid="app-shell">
      <a href="#ca-main-content" className="ca-skip-link">Saltar al contenido principal</a>
      {open && (
        <button
          aria-label="Cerrar menú"
          className="ca-sidebar-backdrop lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        data-testid="app-sidebar"
        aria-label="Menú principal"
        className={`ca-sidebar ${open ? 'is-open' : ''}`}
      >
        <div className="ca-brand">
          <Logo size="sm" />
          <button
            aria-label="Cerrar menú"
            className="ca-icon-button lg:hidden"
            onClick={() => setOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <div className="ca-sidebar-status">
          <span className="ca-live-dot" />
          <div>
            <strong>Centro de Operaciones</strong>
            <span>Servicio activo 24/7</span>
          </div>
        </div>

        <nav ref={navRef} aria-label="Secciones de la aplicación" data-testid="app-sidebar-nav" className="ca-sidebar-nav scrollbar-thin">
          {groups.map((group) => (
            <section key={group.label} className="ca-nav-group">
              <p className="ca-nav-heading">{group.label}</p>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) => `ca-nav-link ${isActive ? 'is-active' : ''}`}
                >
                  <span className="ca-nav-icon"><item.icon size={19} strokeWidth={1.9} /></span>
                  <span>{item.label}</span>
                  <ChevronRight className="ca-nav-arrow" size={15} />
                </NavLink>
              ))}
            </section>
          ))}
        </nav>

        <div className="ca-sidebar-footer">
          {isReadOnly(user) && <div data-testid="readonly-banner" className="ca-readonly">Modo solo lectura</div>}
          <div className="ca-profile">
            <div className="ca-avatar">{user?.name?.[0] || 'U'}</div>
            <div className="ca-profile-copy">
              <strong>{user?.name || 'Usuario'}</strong>
              <span data-testid="user-role-label">{roleLabel(user?.role)}</span>
            </div>
            <button onClick={() => nav('/change-password')} data-testid="btn-change-password" aria-label="Cambiar contraseña" className="ca-profile-action">
              <KeyRound size={17} />
            </button>
            <button onClick={logout} data-testid="btn-logout" aria-label="Cerrar sesión" className="ca-profile-action danger">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <div className="ca-workspace">
        <header className="ca-topbar">
          <button onClick={() => setOpen(true)} aria-label="Abrir menú" className="ca-icon-button lg:hidden">
            <Menu size={22} />
          </button>

          <div className="ca-page-heading">
            <span>Centro de Operaciones</span>
            <h1>{title}</h1>
          </div>

          <FleetAlertsBanner />

          <div className="ca-datetime" aria-label={`${formattedDate}, ${formattedTime}`}>
            <span><CalendarDays size={15} />{formattedDate}</span>
            <strong><Clock3 size={16} />{formattedTime}</strong>
          </div>

          <div className="ca-topbar-actions">
            {canCreate && (
              <button onClick={() => nav('/dispatch/nuevo')} data-testid="btn-new-service" className="ca-new-service">
                <Plus size={18} />
                <span className="hidden sm:inline">Nuevo servicio</span>
              </button>
            )}
            <NotificationsBell />
          </div>
        </header>

        <main id="ca-main-content" tabIndex="-1" className="app-main ca-main scrollbar-thin" data-testid="app-main">
          <OperationalServiceReminder />
          {children}
        </main>
      </div>

      <nav className="ca-mobile-nav lg:hidden" aria-label="Navegación principal">
        {mobileItems.map((item) => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => `ca-mobile-link ${isActive ? 'is-active' : ''}`}>
            <item.icon size={20} strokeWidth={2} />
            <span>{item.shortLabel || item.label}</span>
          </NavLink>
        ))}
        {canCreate && (
          <button onClick={() => nav('/dispatch/nuevo')} className="ca-mobile-create" aria-label="Crear servicio">
            <Plus size={24} />
          </button>
        )}
      </nav>
    </div>
  );
}
